/**
 * Processador de dados Excel - Modelo Levantadas de Mão
 * Cada linha representa uma janela de conversão (levantada de mão)
 */

/**
 * Processa os dados brutos do Excel focando em levantadas de mão
 * Lê os valores pelo cabeçalho da planilha (linha 0)
 * @param {Array} jsonData - Dados brutos do Excel (sheet_to_json header:1)
 * @returns {Array} - Dados processados por levantada de mão
 */
export const processExcelData = (jsonData) => {
  if (!jsonData || !Array.isArray(jsonData) || jsonData.length < 2) {
    return [];
  }

  const headers = (jsonData[0] || []).map(h => String(h || '').trim());

  // Normaliza cabeçalhos para facilitar matching
  const normalize = (s) => String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '');

  const normalizedHeaders = headers.map(h => normalize(h));

  const findIndex = (candidates) => {
    const keys = Array.isArray(candidates) ? candidates : [candidates];
    for (const key of keys) {
      const idx = normalizedHeaders.indexOf(normalize(key));
      if (idx !== -1) return idx;
    }
    return -1;
  };

  // Mapeia índices com base no cabeçalho informado no prompt
  const idx = {
    idConversao: findIndex(['id conversao', 'idconversao', 'id_conv', 'id']),
    levantadaInicio: findIndex(['levantada_mao_inicio', 'levantadamaoinicio']),
    levantadaFim: findIndex(['levantada_mao_fim', 'levantadamaofim']),
    leadTelefone: findIndex(['lead_telefone', 'telefone', 'leadtelefone']),
    leadNome: findIndex(['lead_nome', 'nome', 'leadnome']),
    leadEmail: findIndex(['lead_email', 'email', 'leademail']),
    origem: findIndex(['origem', 'campanha', 'fluxo']),
    atendenteEmail: findIndex(['atendente_email', 'atendenteemail', 'email_atendente']),
    tickets: findIndex(['tickets', 'links', 'conversas']),
    dataMensagemLead: findIndex(['data_mensagem_lead', 'datamensagemlead']),
    dataMensagemAtendente: findIndex(['data_mensagem_atendente', 'datamensagematendente']),
    dataTransbordo: findIndex(['dataTransbordo', 'data_transbordo', 'datatransbordo'])
  };

  const rows = jsonData.slice(1);

  return rows
    .map((row) => {
      if (!row || !Array.isArray(row)) return null;

      const get = (i) => (i >= 0 ? row[i] : null);

      const idConversao = String(get(idx.idConversao) || '').trim();
      const telefone = String(get(idx.leadTelefone) || '').trim();
      const nomeCliente = String(get(idx.leadNome) || '').trim();
      const email = String(get(idx.leadEmail) || '').trim();
      const origem = String(get(idx.origem) || '').trim();

      const levantadaMaoInicio = parseDate(get(idx.levantadaInicio));
      const levantadaMaoFim = parseDate(get(idx.levantadaFim));

      const ticketsRaw = String(get(idx.tickets) || '').trim();
      const tickets = ticketsRaw
        ? ticketsRaw.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      const atendenteResponsavel = String(get(idx.atendenteEmail) || '').trim() || 'Não atribuído';

      const dataTransbordo = parseDate(get(idx.dataTransbordo));
      const dataMensagemLead = parseDate(get(idx.dataMensagemLead));
      const dataMensagemAtendente = parseDate(get(idx.dataMensagemAtendente));

      // Calcula tempo de resposta em minutos, se possível
      let minutosResposta = null;
      if (dataMensagemLead && dataMensagemAtendente) {
        minutosResposta = Math.max(0, Math.round((dataMensagemAtendente - dataMensagemLead) / (1000 * 60)));
      }

      // Compatibilidade com visuais atuais do modal (usa truthy de strings)
      const mensagemLead = dataMensagemLead ? 'Lead enviou mensagem' : '';
      const respostaAtendente = dataMensagemAtendente ? 'Atendente respondeu' : '';

      const temMensagemLead = !!dataMensagemLead;
      const temRespostaAtendente = !!dataMensagemAtendente;
      const foiRespondida = temMensagemLead && temRespostaAtendente;
      const naoFoiRespondida = temMensagemLead && !temRespostaAtendente;

      let duracaoJanela = null;
      if (levantadaMaoInicio && levantadaMaoFim) {
        duracaoJanela = (levantadaMaoFim - levantadaMaoInicio) / (1000 * 60 * 60);
      }

      const linkConversa = tickets[0] || '';
      const linkConversaTexto = linkConversa;

      const id = idConversao || `${telefone}_${String(levantadaMaoInicio || '')}`;

      return {
        id,
        telefone,
        nomeCliente,
        email,
        origem,
        levantadaMaoInicio,
        levantadaMaoFim,
        duracaoJanela,
        tickets,
        totalTickets: tickets.length,
        atendenteResponsavel,
        dataTransbordo,
        dataAceite: null,
        dataMensagemLead,
        dataMensagemAtendente,
        mensagemLead,
        respostaAtendente,
        minutosResposta,
        linkConversaTexto,
        linkConversa,
        temMensagemLead,
        temRespostaAtendente,
        foiRespondida,
        naoFoiRespondida,
        // Alias para compatibilidade com hook de filtros
        temResposta: foiRespondida,
        dataConversao: levantadaMaoInicio
      };
    })
    .filter(item => !!item && (item.telefone || item.id));
};

/**
 * Função auxiliar para parsing seguro de datas
 * @param {any} dateValue - Valor da data
 * @returns {Date|null} - Data válida ou null
 */
function parseDate(dateValue) {
  if (!dateValue) return null;
  
  try {
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  } catch (e) {
    return null;
  }
}

/**
 * Valida se o arquivo é um Excel válido
 * @param {File} file - Arquivo para validar
 * @returns {boolean} - True se válido
 */
export const validateExcelFile = (file) => {
  if (!file) return false;
  return file.name.match(/\.(xlsx|xls)$/i);
};

/**
 * Formata tempo em minutos para formato legível
 * @param {number} minutos - Tempo em minutos
 * @returns {string} - Tempo formatado
 */
export const formatTempo = (minutos) => {
  if (!minutos || minutos < 0) return '0min';
  if (minutos < 60) return `${Math.round(minutos)}min`;
  
  const horas = Math.floor(minutos / 60);
  const mins = Math.round(minutos % 60);
  return `${horas}h ${mins}min`;
};

/**
 * Formata duração em horas para formato legível
 * @param {number} horas - Duração em horas
 * @returns {string} - Duração formatada
 */
export const formatDuracao = (horas) => {
  if (!horas || horas < 0) return '0h';
  if (horas < 1) return `${Math.round(horas * 60)}min`;
  
  const h = Math.floor(horas);
  const mins = Math.round((horas % 1) * 60);
  return mins > 0 ? `${h}h ${mins}min` : `${h}h`;
};